import { useEffect, useMemo, useState } from "react";
import { Download, Eye, Filter, RotateCcw } from "lucide-react";
import { API_EMPLOYEE_CERTIFICATION_EXPORT } from "../../../../config/Endpoints.js";

import { Card } from "../../../components/shared/card/card.tsx";
import { EmptyStateMessage } from "../../../components/shared/empty-state-message/empty-state-message.tsx";
import { EmployeeTableAvatar } from "../../../components/features/approve-bank-details/components/employee-table-avatar.tsx";
import {
  Badge,
  Button,
  ResponsiveDataTable,
  Select,
  Spinner,
  type ResponsiveColumn,
} from "../../../components/ui/index.ts";
import { useEmployeeNameOptions } from "../../../hooks/use-employee-name-options.ts";
import { showError } from "../../../utils/toast.ts";
import { CertificationEditorModal } from "./certification-editor-modal.tsx";
import type {
  CertificationFilters,
  EmployeeCertificationRow,
} from "./certification-types.ts";
import {
  useCertificationList,
  useCertificationMaster,
} from "./use-certification-list.ts";
import styles from "../tech-stack-list-page.module.css";
import { useSkillsExport } from "../use-skills-export.ts";

const EMPTY_FILTERS: CertificationFilters = {
  employeeId: "",
  category: "",
  certificationId: "",
  certificateUploaded: "",
};

function formatLabel(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function CertificationListTab() {
  const employees = useEmployeeNameOptions();
  const master = useCertificationMaster();
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedEmployee, setSelectedEmployee] =
    useState<EmployeeCertificationRow | null>(null);
  const list = useCertificationList(filters, page, pageSize);
  const exportAction = useSkillsExport();

  useEffect(() => {
    const message = employees.error || master.error || list.error;
    if (message) showError(message);
  }, [employees.error, list.error, master.error]);

  useEffect(() => {
    const cleared =
      !draftFilters.employeeId &&
      !draftFilters.category &&
      !draftFilters.certificationId &&
      !draftFilters.certificateUploaded;
    const applied =
      Boolean(filters.employeeId) ||
      Boolean(filters.category) ||
      Boolean(filters.certificationId) ||
      Boolean(filters.certificateUploaded);
    if (cleared && applied) {
      setFilters(EMPTY_FILTERS);
      setPage(1);
    }
  }, [draftFilters, filters]);

  const categoryOptions = useMemo(
    () =>
      master.categories.map((category) => ({
        value: category.category,
        label: formatLabel(category.category),
      })),
    [master.categories],
  );

  const certificationOptions = useMemo(
    () =>
      master.categories
        .filter(
          (category) =>
            !draftFilters.category ||
            category.category === draftFilters.category,
        )
        .flatMap((category) =>
          category.certifications.map((certification) => ({
            value: String(certification.certificationId),
            label: certification.certificationName,
          })),
        ),
    [draftFilters.category, master.categories],
  );

  const columns = useMemo<ResponsiveColumn<EmployeeCertificationRow>[]>(
    () => [
      {
        id: "employeeName",
        header: "Employee",
        accessor: "employeeName",
        sortable: true,
        render: (row) => (
          <div className={styles.employeeCell}>
            <EmployeeTableAvatar
              employeeId={row.employeeId}
              name={row.employeeName}
              photoPath={row.photoPath ?? undefined}
              size="md"
            />
            <span>
              <strong className={styles.employeeName}>{row.employeeName}</strong>
              <small className={styles.employeeCode}>
                {row.employeeCode || "-"}
              </small>
            </span>
          </div>
        ),
      },
      {
        id: "designation",
        header: "Designation",
        accessor: "designation",
        sortable: true,
        truncate: true,
      },
      {
        id: "manager",
        header: "Reporting Manager",
        accessor: "manager",
        sortable: true,
        truncate: true,
      },
      {
        id: "certifications",
        header: "Certifications",
        getTitleText: (row) =>
          row.certifications.map((item) => item.certification).join(", "),
        render: (row) =>
          row.certifications.length ? (
            <div className={styles.badgeList}>
              {row.certifications.slice(0, 3).map((item) => (
                <Badge
                  key={item.employeeCertificationId ?? item.certificationId}
                  variant={item.verified ? "success" : "neutral"}
                >
                  {item.certification}
                </Badge>
              ))}
              {row.certifications.length > 3 ? (
                <Badge variant="neutral">
                  +{row.certifications.length - 3}
                </Badge>
              ) : null}
            </div>
          ) : (
            <span className={styles.noSkills}>Not Uploaded</span>
          ),
      },
      {
        id: "action",
        header: "Action",
        nowrap: true,
        render: (row) => (
          <Button
            size="sm"
            variant="accent-ghost"
            startIcon={<Eye size={16} aria-hidden />}
            onClick={() => setSelectedEmployee(row)}
          >
            View
          </Button>
        ),
      },
    ],
    [],
  );

  const lookupLoading = employees.loading || master.loading;
  const clearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const exportCertifications = () => {
    const params = new URLSearchParams();
    if (draftFilters.employeeId) {
      params.set("employeeId", draftFilters.employeeId);
    }
    if (draftFilters.category) {
      params.set("category", draftFilters.category);
    }
    if (draftFilters.certificationId) {
      params.set("certificationId", draftFilters.certificationId);
    }
    if (
      draftFilters.certificateUploaded === "true" ||
      draftFilters.certificateUploaded === "false"
    ) {
      params.set("certificateUploaded", draftFilters.certificateUploaded);
    }
    void exportAction.exportExcel(
      API_EMPLOYEE_CERTIFICATION_EXPORT,
      params,
      "Employee_Certifications.xlsx",
    );
  };

  return (
    <>
      <Card className={styles.filterCard}>
        <div className={styles.filters}>
          <Select
            id="certification-employee-filter"
            label="Employee Name"
            placeholder={employees.loading ? "Loading..." : "Select Employee"}
            value={draftFilters.employeeId}
            onChange={(employeeId) =>
              setDraftFilters((current) => ({ ...current, employeeId }))
            }
            options={employees.options}
            disabled={employees.loading}
            searchable
            isClearable
          />
          <Select
            id="certification-category-filter"
            label="Category"
            placeholder={master.loading ? "Loading..." : "Select Category"}
            value={draftFilters.category}
            onChange={(category) =>
              setDraftFilters((current) => ({
                ...current,
                category,
                certificationId: "",
              }))
            }
            options={categoryOptions}
            disabled={master.loading}
            searchable
            isClearable
          />
          <Select
            id="certification-filter"
            label="Certification"
            placeholder={
              master.loading ? "Loading..." : "Select Certification"
            }
            value={draftFilters.certificationId}
            onChange={(certificationId) =>
              setDraftFilters((current) => ({
                ...current,
                certificationId,
              }))
            }
            options={certificationOptions}
            disabled={master.loading}
            searchable
            isClearable
          />
          <Select
            id="certificateUploaded-filter"
            label="Certifications Status"
            placeholder="Select Status"
            value={draftFilters.certificateUploaded}
            onChange={(certificateUploaded) =>
              setDraftFilters((current) => ({ ...current, certificateUploaded }))
            }
            options={[
              { value: "true", label: "Uploaded" },
              { value: "false", label: "Not Uploaded" },
            ]}
            isClearable
          />
          <div className={styles.filterActions}>
            <Button
              startIcon={<Filter size={16} aria-hidden />}
              disabled={lookupLoading || list.loading}
              onClick={() => {
                setFilters({ ...draftFilters });
                setPage(1);
              }}
            >
              Apply Filters
            </Button>
            <Button
              variant="light-grey"
              startIcon={<RotateCcw size={16} aria-hidden />}
              disabled={lookupLoading || list.loading}
              onClick={clearFilters}
            >
              Clear
            </Button>
            <Button
              variant="accent"
              startIcon={<Download size={16} aria-hidden />}
              disabled={lookupLoading || list.loading || exportAction.exporting}
              onClick={exportCertifications}
            >
              {exportAction.exporting ? "Exporting..." : "Export"}
            </Button>
          </div>
        </div>
      </Card>

      <Card padding="none" className={styles.tableCard}>
        {list.loading ? (
          <div className={styles.loading}>
            <Spinner size="lg" label="Loading employee certifications" />
          </div>
        ) : (
          <ResponsiveDataTable<EmployeeCertificationRow>
            columns={columns}
            data={list.rows}
            aria-label="Employee certification list"
            emptyState={
              <EmptyStateMessage message="No employees match the selected filters." />
            }
            pagination
            page={page}
            pageSize={pageSize}
            totalItems={list.totalElements}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            pageSizeOptions={[5, 10, 20, 30, 50]}
          />
        )}
      </Card>

      <CertificationEditorModal
        employee={selectedEmployee}
        categories={master.categories}
        onClose={() => setSelectedEmployee(null)}
        onSaved={list.refetch}
      />
    </>
  );
}
